import React from "react";
import { Instagram, Facebook, MapPin, Phone, Mail } from "lucide-react";
import { SALON, SERVICE_CATEGORIES } from "../data/salonData";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-roseGoldDeep text-white pt-20 pb-10 mt-12 relative overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-roseGold/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="inline-flex bg-white rounded-2xl p-3 shadow-soft">
            <img
              src="/brand/logo.png"
              alt="Kinjal's Beauty & Hair logo"
              className="h-16 w-auto"
              loading="lazy"
            />
          </div>
          <p className="mt-5 text-sm text-white/75 leading-relaxed max-w-sm">
            Premium beauty &amp; hair rituals in Glen Eden, Auckland — delivered
            with warmth, hygiene and an eye for detail.
          </p>

          <div className="mt-6 flex gap-3">
            <a
              href={SALON.socials.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram"
              className="w-10 h-10 rounded-full bg-white text-black hover:bg-roseGold hover:text-white flex items-center justify-center transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href={SALON.socials.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-facebook"
              className="w-10 h-10 rounded-full bg-white text-black hover:bg-roseGold hover:text-white flex items-center justify-center transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Quick links */}
        <div className="md:col-span-2">
          <h4 className="font-display text-sm uppercase tracking-[0.2em] text-white font-bold">
            Salon
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            {[
              ["Home", "#home"],
              ["About", "#about"],
              ["Why Us", "#why-us"],
              ["FAQ", "#faq"],
              ["Contact", "#contact"],
            ].map(([label, href]) => (
              <li key={href}>
                <a
                  href={href}
                  data-testid={`footer-link-${label.toLowerCase()}`}
                  className="text-white/75 hover:text-white transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className="md:col-span-3">
          <h4 className="font-display text-sm uppercase tracking-[0.2em] text-white font-bold">
            Services
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            {SERVICE_CATEGORIES.map((c) => (
              <li key={c.id}>
                <a
                  href="#services"
                  data-testid={`footer-service-${c.id}`}
                  className="text-white/75 hover:text-white transition-colors"
                >
                  {c.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-3">
          <h4 className="font-display text-sm uppercase tracking-[0.2em] text-white font-bold">
            Contact
          </h4>
          <ul className="mt-4 space-y-3 text-sm text-white/90">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-white mt-0.5 shrink-0" />
              <span>{SALON.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-white mt-0.5 shrink-0" />
              <a href={`tel:${SALON.phoneTel}`} className="hover:text-white">
                {SALON.phoneDisplay}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-white mt-0.5 shrink-0" />
              <a href={`mailto:${SALON.email}`} className="hover:text-white break-all">
                {SALON.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-white/60">
          © {new Date().getFullYear()} Kinjal&rsquo;s Beauty &amp; Hair · Glen Eden, Auckland.
          All rights reserved.
        </p>
        <p className="text-xs text-white/50 font-serif-italic">
          Where does beauty begin — right here.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
